/**
 * assets/page-backcountry.js
 * Manages the interactive elements of the Backcountry Portal campaign page.
 * Handles smooth scrolling (Lenis) and intersection observers for fade-in animations.
 */
const BackcountryManager = {
  settings: {
    momentumDuration: 1.2,
    selectors: {
      parallaxHero: '.backcountry-parallax-hero-image',
      parallaxImagesContainer: '#parallax-images-container',
      parallaxImagesFast: '.backcountry-parallax-image',
      parallaxImagesSlow: '.backcountry-parallax-image-slow',
    }
  },
  
  state: {
    lenis: null,
    listeners: [],
    parallaxCache: [],
    gridParallaxCache: null, 
    windowHeight: window.innerHeight,
    isDesktop: window.matchMedia('(min-width: 768px)').matches
  },
  
  /**
   * Initializes the Backcountry Manager.
   * Sets up smooth scrolling, image loaders, UI components, and observers.
   * @returns {void}
   */
  init() {
    this.initSmoothScroll();
    this.initScrollObservers();

    // Calculate positions immediately
    this.updateParallaxCache();

    // Re-calculate on window resize
    window.addEventListener('resize', () => {
      this.state.windowHeight = window.innerHeight;
      this.state.isDesktop = window.matchMedia('(min-width: 768px)').matches;
      
      if (!this.state.isDesktop) {
      // If on mobile, remove parallax transforms so images reset to natural layout
        const { selectors } = this.settings;
        const container = document.querySelector(selectors.parallaxImagesContainer);
        if (container) {
          const images = [
            ...container.querySelectorAll(selectors.parallaxImagesFast),
            ...container.querySelectorAll(selectors.parallaxImagesSlow)
          ];
          images.forEach(img => {
            img.style.transform = '';
            // clear will-change to save memory on mobile
            img.style.willChange = 'auto'; 
          });
        }
      }

      this.updateParallaxCache();
      // Also tell Lenis to recalculate bounds
      if (this.state.lenis) this.state.lenis.resize(); 
    });

    // Refresh once more slightly later to ensure layout is settled
    setTimeout(() => {
      this.updateParallaxCache();
      if (this.state.lenis) this.state.lenis.resize();
    }, 100);
  },

  /**
   * Initializes Smooth Scroll (momentum scrolling) using Lenis.
   * Sets up the Request Animation Frame loop and binds the scroll event to parallax.
   * @returns {void}
   */
  initSmoothScroll() {
    if (this.state.lenis) return;

    this.state.lenis = new Lenis({
      duration: this.settings.momentumDuration,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });

    const raf = (time) => {
      this.state.lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    this.state.lenis.on('scroll', (e) => {
      this.handleGlobalParallax(e.scroll);
    });
  },

  /**
   * Calculates the position and dimensions of all parallax elements and stores them in state.
   * This prevents DOM thrashing (repeated getBoundingClientRect calls) during the scroll loop.
   * Should be called on init and resize.
   * @returns {void}
   */
  updateParallaxCache() {
    const { selectors } = this.settings;

    const parallaxHeroImages = document.querySelectorAll(selectors.parallaxHero);
    // Get current scroll position to calculate absolute top
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    this.state.parallaxCache = Array.from(parallaxHeroImages).map((img) => {
      const container = img.closest('section') || img.parentElement;
      if (!container) return null;
      
      // Hint to the browser that this element's transform will change
      img.style.willChange = 'transform';

      const rect = container.getBoundingClientRect();
      
      return {
        el: img,
        // Absolute top position relative to the document
        top: rect.top + scrollTop, 
        height: rect.height
      };
    }).filter(item => item !== null); 

    const parallaxImagesContainer = document.querySelector(selectors.parallaxImagesContainer);
    
    if (parallaxImagesContainer) {
      const fastImages = parallaxImagesContainer.querySelectorAll(selectors.parallaxImagesFast);
      const slowImages = parallaxImagesContainer.querySelectorAll(selectors.parallaxImagesSlow);
      const rect = parallaxImagesContainer.getBoundingClientRect();

      // Hint to the browser that this element's transform will change
      fastImages.forEach(img => img.style.willChange = 'transform');
      slowImages.forEach(img => img.style.willChange = 'transform');

      this.state.gridParallaxCache = {
        containerTop: rect.top + scrollTop,
        containerHeight: rect.height,
        fastImages: fastImages,
        slowImages: slowImages
      };
    } else {
      this.state.gridParallaxCache = null;
    }
  },

  /**
   * Updates the transform property of parallax elements based on scroll position.
   * Uses cached dimensions for performance.
   * @param {number} currentScroll - The current vertical scroll position (passed from Lenis).
   * @returns {void}
   */
  handleGlobalParallax(currentScroll) {
    const { windowHeight, parallaxCache, gridParallaxCache, isDesktop } = this.state;
    
    parallaxCache.forEach((item) => {
      const isVisible = (currentScroll + windowHeight) > item.top && 
                        currentScroll < (item.top + item.height);

      if (isVisible) {
        const relativeScroll = currentScroll - item.top;
        const speedFactor = 0.1;
        item.el.style.transform = `translate3d(0, ${relativeScroll * speedFactor}px, 0)`;
      }
    });

    // Only run if the container exists AND we are on desktop (min-width: 768px)
    if (gridParallaxCache && isDesktop) {
      const { containerTop, containerHeight, fastImages, slowImages } = gridParallaxCache;
      
      const isVisible = (currentScroll + windowHeight) > containerTop && currentScroll < (containerTop + containerHeight);

      if (isVisible) {
        const relativeScroll = currentScroll - containerTop;
        const speedFactor = 0.20;
        const speedFactorSlow = 0.1;

        // Animate the 'fast' images
        fastImages.forEach((img) => {
          img.style.transform = `translate3d(0, ${relativeScroll * speedFactor}px, 0)`;
        });

        // Animate the 'slow' images
        slowImages.forEach((img) => {
          img.style.transform = `translate3d(0, ${relativeScroll * speedFactorSlow}px, 0)`;
        });
      }
    }
  },

  /**
   * Initializes Intersection Observers for fade-on-scroll animations.
   * Adds 'is-visible' class when elements enter the viewport.
   * * @returns {void}
   */
  initScrollObservers() {
    const elements = document.querySelectorAll('.fade-on-scroll');
    if (elements.length === 0) return;

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    elements.forEach(el => observer.observe(el));
  }
};

// Init on load
document.addEventListener('DOMContentLoaded', () => BackcountryManager.init());

// Init on 'Theme Editor' change (a Shopify specific event)
document.addEventListener('shopify:section:load', () => BackcountryManager.init());
